import type { LandingData } from "../../api/types";
import { fmt } from "../../domain/format";
import { kpiTrend } from "../../domain/kpi-trend";
import { profitPeriod } from "../../domain/profit-period";
import { toISO, lastNDays } from "../../domain/date-range";
import { Logo, Icon, IntegrationGlyph } from "../../components/ui.jsx";
import { INTEGRACOES_HERO, INTEGRACOES_EM_BREVE } from "./landing.data";

const F = fmt;

const NAV_LINKS: ReadonlyArray<{ href: string; rotulo: string }> = [
  { href: "#problema", rotulo: "ROAS + lucro" },
  { href: "#meu-lucro", rotulo: "Módulos" },
  { href: "/blog", rotulo: "Blog" },
  { href: "#comecar", rotulo: "Comece agora" },
  { href: "#faq", rotulo: "FAQ" },
];

/** Header sticky da landing — escuro, fora do <main> (landmark próprio). */
export function LandingHeader({
  onTestar,
  onEntrar,
}: {
  onTestar: () => void;
  onEntrar: () => void;
}) {
  return (
    <header className="lp-topnav">
      <div
        className="lp-wrap"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: 70,
          gap: 12,
        }}
      >
        <Logo size={32} color="#fff" />
        <nav
          className="lp-nav"
          aria-label="Navegação principal"
          style={{ display: "flex", alignItems: "center", gap: 28 }}
        >
          {NAV_LINKS.map((l) => (
            <a key={l.href} href={l.href} className="lp-link">
              {l.rotulo}
            </a>
          ))}
        </nav>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <button className="btn btn-ghost btn-sm" data-cta="header_login" onClick={onEntrar}>
            Entrar
          </button>
          <button className="btn btn-primary btn-sm" data-cta="header_signup" onClick={onTestar}>
            Ver demonstração
          </button>
        </div>
      </div>
      {/* ≤900px: a nav some e este trilho de âncoras assume */}
      <nav className="lp-nav-mobile" aria-label="Seções da página">
        {NAV_LINKS.map((l) => (
          <a key={l.href} href={l.href} className="lp-link">
            {l.rotulo}
          </a>
        ))}
      </nav>
    </header>
  );
}

/** Chip de variação — a SETA segue a direção; a COR segue o que é bom: resultado
 *  (vendas/lucro) subir é verde; custo (custos/mídia) subir é vermelho. */
function DeltaChip({ delta, resultado }: { delta: number | null; resultado: boolean }) {
  if (delta == null) return null;
  const up = delta >= 0;
  const bom = resultado ? up : !up;
  return (
    <span className={"lp-kpi-delta " + (bom ? "good" : "bad")}>
      <Icon name={up ? "arrowUp" : "arrowDown"} size={11} color="currentColor" strokeWidth={2.8} />
      {F.pct(Math.abs(delta), { dec: 0 })}
    </span>
  );
}

/** Delta do card diário — a SETA segue a direção; a COR segue o que é bom:
 *  resultado (vendas/lucro) subir é verde; custo (custos/mídia) subir é vermelho. */
function DayDelta({ delta, resultado }: { delta: number | null; resultado: boolean }) {
  if (delta == null) return <span className="lp-hero-day-delta" />;
  const up = delta >= 0;
  const bom = resultado ? up : !up;
  return (
    <span className={"lp-hero-day-delta " + (bom ? "good" : "bad")}>
      <Icon name={up ? "arrowUp" : "arrowDown"} size={10} color="currentColor" strokeWidth={2.8} />
      {F.pct(Math.abs(delta), { dec: 0 })}
    </span>
  );
}

/** Mockup em camadas do hero — painel de KPIs + barras de lucro/dia, com chip de
 *  recomendação e card de margem flutuando. Só os dados do mock (via contrato). */
function HeroMockup({ D }: { D: LandingData }) {
  const serie = D.lucroSerie;
  const maxV = Math.max(...serie.map((p) => p.valor), 1);
  // KPIs + deltas mensais via domain/profit-period (mesma matemática do app/inicio):
  // Total em vendas (bruta) · Total dos Custos (variáveis) · Marketing · Lucro Gerado.
  // `resultado` marca os KPIs de resultado (cor do delta: subir é bom em vendas/lucro,
  // ruim em custos/mídia). Fallback no waterfall caso falte o período.
  const ads = {
    google: D.ads.investimentoGoogle,
    meta: D.ads.investimentoMeta,
    total: D.ads.investimentoTotal,
  };
  const periodoMes = D.derivados.lucroPeriodos["este-mes"];
  const pp = periodoMes
    ? profitPeriod({
        waterfall: D.waterfall,
        periodo: periodoMes,
        anterior: periodoMes.anterior,
        ads,
        mediaConn: true,
      })
    : null;
  const baseStep = D.waterfall.find((s) => s.tipo === "base");
  const totalStep = D.waterfall.find((s) => s.tipo === "total");
  const custosWf = D.waterfall
    .filter((s) => s.tipo === "sub" && s.rotulo !== "Anúncios")
    .reduce((acc, s) => acc + Math.abs(s.valor), 0);
  const kpis = [
    {
      label: "Total em vendas",
      value: F.brl(pp?.vendasBrutas ?? baseStep?.valor ?? D.nuvemshop.receita),
      color: "var(--kpi-vendas)",
      delta: pp?.deltas.vendas ?? D.derivados.deltaReceita,
      resultado: true,
    },
    {
      label: "Total dos Custos",
      value: F.brl(pp?.custos.total ?? custosWf),
      color: "var(--kpi-custos)",
      delta: pp?.deltas.custos ?? null,
      resultado: false,
    },
    {
      label: "Marketing",
      value: F.brl(pp?.marketing.total ?? D.ads.investimentoTotal),
      color: "var(--kpi-marketing)",
      delta: pp?.deltas.marketing ?? null,
      resultado: false,
    },
    {
      label: "Lucro Gerado",
      value: F.brl(pp?.lucroExibido ?? totalStep?.valor ?? D.derivados.lucroLiquido),
      color: "var(--kpi-lucro)",
      delta: pp?.deltas.lucro ?? D.derivados.deltaLucro,
      resultado: true,
    },
  ];
  // Card diário (popup): buckets semanais do gráfico de tendência (kpiTrend → mesmo
  // profit-period). Janela de 28d → buckets semanais; pega o mais recente.
  const buckets = periodoMes
    ? kpiTrend({
        waterfall: D.waterfall,
        ads,
        periodo: periodoMes,
        anterior: periodoMes.anterior,
        mediaConn: true,
        range: lastNDays(toISO(new Date()), 28),
      })
    : [];
  const dia = buckets.at(-1) ?? null;
  const diaLinhas = dia
    ? [
        {
          key: "vendas",
          label: "Total em vendas",
          color: "var(--kpi-vendas)",
          val: dia.vendas,
          delta: dia.deltas?.vendas ?? null,
          resultado: true,
        },
        {
          key: "custos",
          label: "Total dos Custos",
          color: "var(--kpi-custos)",
          val: dia.custos,
          delta: dia.deltas?.custos ?? null,
          resultado: false,
        },
        {
          key: "marketing",
          label: "Marketing",
          color: "var(--kpi-marketing)",
          val: dia.marketing,
          delta: dia.deltas?.marketing ?? null,
          resultado: false,
        },
        {
          key: "lucro",
          label: "Lucro Gerado",
          color: "var(--kpi-lucro)",
          val: dia.lucro,
          delta: dia.deltas?.lucro ?? null,
          resultado: true,
        },
      ]
    : [];
  return (
    <>
      <div className="lp-hero-panel">
        <div className="lp-hero-panel-head">
          <span className="lp-hero-panel-title">Resumo · {D.nuvemshop.loja}</span>
          <span className="lp-hero-conn">
            <span className="lp-hero-conn-dot" /> Nuvemshop conectada · loja de exemplo
          </span>
        </div>
        <div className="lp-hero-kpis">
          {kpis.map((k) => (
            <div key={k.label} className="lp-hero-kpi">
              <span className="lp-hero-kpi-label">
                <span className="lp-hero-kpi-dot" style={{ background: k.color }} />
                {k.label}
              </span>
              <span className="lp-hero-kpi-val">{k.value}</span>
              <DeltaChip delta={k.delta} resultado={k.resultado} />
            </div>
          ))}
        </div>
        <div className="lp-hero-bars" role="img" aria-label="Lucro por dia nos últimos 30 dias">
          {serie.map((p, i) => (
            <span
              key={i}
              className="lp-hero-bar"
              style={{ height: `${Math.max((p.valor / maxV) * 100, 5)}%` }}
            />
          ))}
        </div>
        <div className="lp-hero-bars-cap">Lucro por dia · últimos 30 dias</div>
      </div>

      <div className="lp-hero-float-chip">
        <span className="lp-hero-chip-ic">
          <Icon name="spark" size={14} color="var(--lucro)" />
        </span>
        <span className="lp-hero-chip-body">
          <span className="lp-hero-chip-title">Recomendação do dia</span>
          <span className="lp-hero-chip-text">{D.derivados.respostaDoDia}</span>
        </span>
      </div>

      <div className="lp-hero-float-card">
        <span className="lp-hero-fc-label">Margem das vendas</span>
        <span className="lp-hero-fc-val">{F.pct(D.derivados.margemLiquida, { dec: 0 })}</span>
        <div className="lp-hero-spark" aria-hidden="true">
          {serie.map((p, i) => (
            <span key={i} style={{ height: `${Math.max((p.valor / maxV) * 100, 8)}%` }} />
          ))}
        </div>
      </div>

      {dia && (
        <div className="lp-hero-float-day">
          <span className="lp-hero-day-date">{dia.rotulo}</span>
          {diaLinhas.map((r) => (
            <div key={r.key} className="lp-hero-day-row">
              <span className="lp-hero-day-dot" style={{ background: r.color }} />
              <span className="lp-hero-day-label">{r.label}</span>
              <span className="num lp-hero-day-val">{F.brl(r.val)}</span>
              <DayDelta delta={r.delta} resultado={r.resultado} />
            </div>
          ))}
        </div>
      )}
    </>
  );
}

/** Hero — renderiza imediatamente; só o mockup de produto espera os dados. */
export function LandingHero({ D, onTestar }: { D: LandingData | undefined; onTestar: () => void }) {
  return (
    <div className="lp-hero-bg">
      <section
        className="lp-wrap"
        aria-label="Apresentação"
        style={{ paddingTop: 64, paddingBottom: 64, position: "relative", zIndex: 1 }}
      >
        <div className="lp-hero">
          <div className="rise">
            <span className="lp-hero-pill">
              <Icon name="spark" size={14} color="var(--mint)" />
              Demonstração ao vivo · sem cadastro
            </span>
            <h1 className="lp-hero-h1">
              Seu ROAS subiu.
              <br />
              <span className="lp-hero-accent">E o seu lucro?</span>
            </h1>
            <p className="lp-hero-sub">
              O Lucraa desconta CMV, frete, imposto e mídia de cada venda e mostra o lucro real — por
              campanha e por produto. Direto da sua Nuvemshop, sem planilha. Conexão em 2 minutos,
              primeiro relatório no mesmo dia.
            </p>
            <div className="lp-hero-cta">
              <button className="btn btn-primary btn-lg" data-cta="hero_signup" onClick={onTestar}>
                Ver demonstração ao vivo
              </button>
              <a className="lp-hero-link2" href="#meu-lucro" style={{ textDecoration: "none" }}>
                Ver como funciona <span aria-hidden="true">↓</span>
              </a>
            </div>
            <p
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginTop: 16,
                fontSize: 13,
                color: "rgba(255,255,255,.6)",
              }}
            >
              <Icon name="check" size={13} color="var(--mint)" strokeWidth={2.6} />
              A partir de R$ 197/mês · 7 dias grátis · sem cartão
            </p>
          </div>

          <div className="rise lp-hero-stage" style={{ animationDelay: ".1s" }}>
            {D ? (
              <HeroMockup D={D} />
            ) : (
              <div
                className="lp-hero-panel"
                style={{ minHeight: 300, display: "grid", placeItems: "center" }}
              >
                <span
                  className="spinner spinner-dark"
                  role="status"
                  aria-label="Carregando números"
                />
              </div>
            )}
          </div>
        </div>

        {/* logos integrações — ativas + em breve */}
        <div className="lp-integra">
          <div className="lp-integra-grupo">
            <span className="lp-integra-label">Integrações pelas APIs oficiais</span>
            <ul className="lp-integra-row">
              {INTEGRACOES_HERO.map((i) => (
                <li key={i.glyph}>
                  <IntegrationGlyph kind={i.glyph} size={30} />
                  <span>{i.rotulo}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="lp-integra-grupo">
            <span
              className="pill pill-line"
              style={{
                fontSize: 12,
                color: "rgba(255,255,255,.7)",
                borderColor: "rgba(255,255,255,.25)",
              }}
            >
              Em breve
            </span>
            <ul className="lp-integra-row lp-integra-soon">
              {INTEGRACOES_EM_BREVE.map((i) => (
                <li key={i.glyph}>
                  <IntegrationGlyph kind={i.glyph} size={30} />
                  <span>{i.rotulo}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
}
